import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { hapticImpactSync } from "@/lib/native/capacitorBridge";
import { normalizePath } from "@/lib/native/nativeRoutes";

const EDGE_START_PX = 24;
const BACK_THRESHOLD_PX = 72;
const MAX_VERTICAL_DRIFT_PX = 56;
const ACTIVATION_RATIO = 1.35;
const COMMIT_DURATION_MS = 180;
const CANCEL_DURATION_MS = 220;

const ROOT_BACK_DISABLED_PATHS = new Set(["/", "/auth", "/dashboard"]);

function isInsideOpenOverlay(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[role="dialog"][data-state="open"]') ||
      target.closest('[role="alertdialog"][data-state="open"]') ||
      target.closest('[data-radix-dialog-overlay][data-state="open"]'),
  );
}

function isInteractiveTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"]',
    ),
  );
}

function canGoBack(pathname: string): boolean {
  const path = normalizePath(pathname);
  if (ROOT_BACK_DISABLED_PATHS.has(path)) return false;
  const state = window.history.state as { idx?: number } | null;
  if (typeof state?.idx === "number") return state.idx > 0;
  return window.history.length > 1;
}

function getPageSurface(): HTMLElement | null {
  return document.querySelector(".native-layout-inner");
}

function setPageSurfaceProgress(surface: HTMLElement, dx: number): void {
  const width = Math.max(window.innerWidth, 1);
  const clampedDx = Math.min(Math.max(dx, 0), width);
  const progress = Math.min(clampedDx / width, 1);
  surface.style.transition = "none";
  surface.style.transform = `translate3d(${clampedDx}px, 0, 0)`;
  surface.style.boxShadow = `-${Math.round(18 * progress)}px 0 ${Math.round(
    36 * progress,
  )}px rgb(0 0 0 / ${0.18 * progress})`;
}

function animatePageSurface(
  surface: HTMLElement,
  dx: number,
  durationMs: number,
  onDone?: () => void,
): number {
  const width = Math.max(window.innerWidth, 1);
  const progress = Math.min(Math.max(dx, 0) / width, 1);
  surface.style.transition = `transform ${durationMs}ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow ${durationMs}ms ease`;
  surface.style.transform = `translate3d(${dx}px, 0, 0)`;
  surface.style.boxShadow = `-${Math.round(18 * progress)}px 0 ${Math.round(
    36 * progress,
  )}px rgb(0 0 0 / ${0.18 * progress})`;

  return window.setTimeout(() => {
    onDone?.();
  }, durationMs);
}

function resetPageSurface(surface: HTMLElement | null): void {
  if (!surface) return;
  surface.style.transition = "";
  surface.style.transform = "";
  surface.style.boxShadow = "";
}

/**
 * Capacitor iOS runs the app inside one WKWebView, so React Router hash
 * navigation does not get UIKit's native interactive-pop gesture. This hook
 * maps a left-edge swipe to one router back step.
 */
export function useNativeEdgeSwipeBack() {
  const { isNative } = useNativePlatform();
  const navigate = useNavigate();
  const location = useLocation();
  const startX = useRef(0);
  const startY = useRef(0);
  const currentDx = useRef(0);
  const tracking = useRef(false);
  const visualStarted = useRef(false);
  const activated = useRef(false);
  const surfaceRef = useRef<HTMLElement | null>(null);
  const animationTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!isNative || Capacitor.getPlatform() !== "ios") return;

    const clearAnimationTimer = () => {
      if (animationTimer.current == null) return;
      window.clearTimeout(animationTimer.current);
      animationTimer.current = null;
    };

    const resetGesture = () => {
      tracking.current = false;
      visualStarted.current = false;
      activated.current = false;
      currentDx.current = 0;
      surfaceRef.current = null;
      document.documentElement.classList.remove("native-edge-swipe-active");
    };

    const onTouchStart = (event: TouchEvent) => {
      clearAnimationTimer();
      resetPageSurface(surfaceRef.current);
      resetGesture();

      if (event.touches.length !== 1) return;
      if (!canGoBack(location.pathname)) return;
      if (isInsideOpenOverlay(event.target)) return;
      if (isInteractiveTextTarget(event.target)) return;

      const touch = event.touches[0];
      if (touch.clientX > EDGE_START_PX) return;

      startX.current = touch.clientX;
      startY.current = touch.clientY;
      surfaceRef.current = getPageSurface();
      tracking.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking.current || event.touches.length !== 1) return;

      const touch = event.touches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;
      const absDy = Math.abs(dy);

      if (dx < 0 || absDy > MAX_VERTICAL_DRIFT_PX) {
        const surface = surfaceRef.current;
        if (visualStarted.current && surface) {
          animatePageSurface(surface, 0, CANCEL_DURATION_MS, () => {
            resetPageSurface(surface);
          });
        }
        resetGesture();
        return;
      }

      const isHorizontalSwipe = dx > 16 && dx > absDy * ACTIVATION_RATIO;
      if (!isHorizontalSwipe) return;

      event.preventDefault();
      currentDx.current = dx;
      visualStarted.current = true;
      document.documentElement.classList.add("native-edge-swipe-active");

      const surface = surfaceRef.current;
      if (surface) {
        setPageSurfaceProgress(surface, dx);
      }

      if (dx >= BACK_THRESHOLD_PX && !activated.current) {
        activated.current = true;
        hapticImpactSync("light");
      }
    };

    const onTouchEnd = () => {
      if (!tracking.current) return;
      const shouldGoBack =
        currentDx.current >= BACK_THRESHOLD_PX && canGoBack(location.pathname);
      const surface = surfaceRef.current;

      if (shouldGoBack) {
        const width = Math.max(window.innerWidth, currentDx.current);
        if (surface && visualStarted.current) {
          animationTimer.current = animatePageSurface(
            surface,
            width,
            COMMIT_DURATION_MS,
            () => {
              navigate(-1);
              requestAnimationFrame(() => {
                resetPageSurface(surface);
                resetGesture();
              });
            },
          );
          return;
        }
        resetGesture();
        navigate(-1);
        return;
      }

      if (surface && visualStarted.current) {
        animationTimer.current = animatePageSurface(
          surface,
          0,
          CANCEL_DURATION_MS,
          () => {
            resetPageSurface(surface);
            resetGesture();
          },
        );
        return;
      }

      resetGesture();
    };

    const onTouchCancel = () => {
      const surface = surfaceRef.current;
      if (surface && visualStarted.current) {
        animatePageSurface(surface, 0, CANCEL_DURATION_MS, () => {
          resetPageSurface(surface);
        });
      }
      resetGesture();
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchCancel);

    return () => {
      clearAnimationTimer();
      resetPageSurface(surfaceRef.current);
      resetGesture();
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [isNative, location.pathname, navigate]);
}
